import { useCallback, useEffect, useRef } from 'react'
import { emitBarSelected, emitGranularityChanged, emitPeriodStepped } from './cashflowAnalytics'
import type { CashflowView, Granularity } from './cashflowPeriod'
import type { ViewChange } from './CashflowScreen'

/** A keyboard run through the chart has settled once this long passes without a key. */
export const BAR_SETTLE_MS = 400

/**
 * How `/cashflow` and its drill-down turn a view change into a URL write and
 * an analytics event — one definition, so the two pages cannot drift (#720).
 *
 * - A bar **pointer** pick, an arrow button or the toggle is one intent: it
 *   pushes, so Back restores the period before, and emits at once.
 * - Arrowing through the chart is a **run**, one intent however many keys it
 *   takes. Its first key pushes and the rest replace, so Back leaves the run in
 *   one step. `cashflow_bar_selected` fires once, when the run settles:
 *   `BAR_SETTLE_MS` without a key, Enter or Space (`commit`), any other change
 *   (which settles the run before it counts itself), the location moving by
 *   anything but the run's own write (a browser Back), or leaving the page.
 *   Without the location check a key just after Back replaced the entry Back
 *   had reached, and that entry was gone.
 *
 * Each step still changes the view, so the period's query runs per step;
 * TanStack Query serves a period already fetched from its cache, so there is
 * no second cache here.
 *
 * `write` puts the view in the URL; `replace` is true only inside a run.
 * `locationKey` is the router location's `key`, which every navigation renews:
 * the hook reads it and never touches the router itself.
 */
export function useViewChange(
  granularity: Granularity,
  write: (next: CashflowView, replace: boolean) => void,
  locationKey: string,
): (next: CashflowView, how: ViewChange) => void {
  // The pending settle of a keyboard run; null when no run is open.
  const run = useRef<ReturnType<typeof setTimeout> | null>(null)
  // True from a run's write until the location it produced arrives.
  const ownWrite = useRef(false)
  const seenKey = useRef(locationKey)

  const settle = useCallback(() => {
    if (run.current === null) return
    clearTimeout(run.current)
    run.current = null
    emitBarSelected()
  }, [])

  // Leaving mid-run still counts the selection the person made.
  useEffect(() => settle, [settle])

  // A location the run did not write (Back, Forward, a link) ends the run, so
  // the next key pushes instead of overwriting where the person went.
  useEffect(() => {
    if (locationKey === seenKey.current) return
    seenKey.current = locationKey
    if (ownWrite.current) ownWrite.current = false
    else settle()
  }, [locationKey, settle])

  return useCallback(
    (next: CashflowView, how: ViewChange) => {
      if (how === 'key') {
        const inRun = run.current !== null
        if (run.current !== null) clearTimeout(run.current)
        run.current = setTimeout(settle, BAR_SETTLE_MS)
        ownWrite.current = true
        write(next, inRun)
        return
      }
      settle()
      // A commit settles the run where it stands; the view is already in the URL.
      if (how === 'commit') return
      if (how === 'granularity') emitGranularityChanged(granularity, next.granularity)
      else if (how === 'pointer') emitBarSelected()
      else emitPeriodStepped(how)
      write(next, false)
    },
    [granularity, write, settle],
  )
}
